// src/webideal-recruit/pages/Auth.jsx
// Multi-view auth: login | forgot-password | register
import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  CheckCircle2,
  Users,
  Zap,
  CalendarCheck,
  ChevronLeft,
  TrendingUp,
  User,
  Phone,
  Building2
} from "lucide-react";
import { backendApi } from "@/api/backendApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { motion } from "framer-motion";

import { useAuth } from "../../context/AuthContext";

export default function Auth() {
  const navigate = useNavigate();
  const { login, user: contextUser, loading: authLoading } = useAuth();

  // view: "login" | "forgot" | "register"
  const [view, setView] = useState("login");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Login fields
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // Forgot password
  const [forgotEmail, setForgotEmail] = useState("");

  // Register fields
  const [regForm, setRegForm] = useState({
    fullname: "",
    email: "",
    company: "",
    phone: "",
    plan: "",
    notes: "",
    password: "",
  });

  // Redirect if already logged in, but ONLY after auth check completes
  useEffect(() => {
    if (!authLoading && contextUser && contextUser.role === "employer") {
      navigate("/recruit/dashboard", { replace: true });
    }
  }, [navigate, contextUser, authLoading]);

  const clearMessages = () => { setError(""); setSuccess(""); };

  // ─── LOGIN ───
  const handleLogin = async (e) => {
    e.preventDefault();
    clearMessages();
    if (!email || !password) {
      setError("Please enter both email and password");
      return;
    }
    setLoading(true);
    try {
      const data = await backendApi.auth.login(email, password);
      login(data);
      navigate("/recruit/dashboard", { replace: true });
    } catch (err) {
      setError(err.message || "Invalid email or password.");
    } finally {
      setLoading(false);
    }
  };

  // ─── FORGOT PASSWORD ───
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    clearMessages();
    if (!forgotEmail) {
      setError("Please enter your email address");
      return;
    }
    setLoading(true);
    try {
      // Simulate sending reset link (backend endpoint can be added later)
      await new Promise(r => setTimeout(r, 1000));
      setSuccess("Password reset link sent! Check your email.");
      setForgotEmail("");
    } catch (err) {
      setError("Failed to send reset link. Try again.");
    } finally {
      setLoading(false);
    }
  };

  // ─── REGISTER ───
  const handleRegister = async (e) => {
    e.preventDefault();
    clearMessages();
    if (!regForm.fullname || !regForm.email || !regForm.company || !regForm.phone || !regForm.password) {
      setError("Please fill in all required fields");
      return;
    }
    setLoading(true);
    try {
      const data = await backendApi.auth.register({
        full_name: regForm.fullname,
        email: regForm.email,
        password: regForm.password,
        mobile: regForm.phone,
        role: "employer",
        work_status: regForm.company,
      });
      login(data);
      navigate("/recruit/dashboard", { replace: true });
    } catch (err) {
      setError(err.message || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { label: "Candidate Management", value: "10,000+", sub: "Access a rich pool of job seekers", icon: Users },
    { label: "Smart Screening", value: "AI Powered", sub: "Filter candidates faster with AI", icon: Zap },
    { label: "Interview Scheduling", value: "Easy & Fast", sub: "Schedule interviews in one click", icon: CalendarCheck },
    { label: "Hiring Success Rate", value: "95%", sub: "Improve your hiring efficiency", icon: TrendingUp },
  ];

  // ──────────────────────────────────────────────
  //  LEFT PANEL CONTENT, switches by view
  // ──────────────────────────────────────────────
  const renderLeftContent = () => {
    // ╔═══════════════════════╗
    // ║   FORGOT PASSWORD     ║
    // ╚═══════════════════════╝
    if (view === "forgot") {
      return (
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }} key="forgot">
          <button onClick={() => { setView("login"); clearMessages(); }} className="flex items-center gap-1 text-sm text-purple-600 hover:text-purple-800 font-medium mb-6">
            <ChevronLeft className="w-4 h-4" /> Back to Sign In
          </button>

          <h1 className="text-3xl font-bold text-gray-900 mb-2">Reset Password</h1>
          <p className="text-gray-500 mb-8">Enter your email to receive a reset link.</p>

          {error && <div className="mb-4 p-3 bg-red-50 border-l-4 border-red-500 text-red-700 text-sm rounded-r-lg">{error}</div>}
          {success && <div className="mb-4 p-3 bg-green-50 border-l-4 border-green-500 text-green-700 text-sm rounded-r-lg">{success}</div>}

          <form onSubmit={handleForgotPassword} className="space-y-5">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700" htmlFor="forgot-email">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input id="forgot-email" type="email" placeholder="name@company.com" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} className="pl-11 h-12 bg-gray-50/50 border-gray-200" autoComplete="email" />
              </div>
            </div>

            <Button type="submit" disabled={loading} className="w-full h-12 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold text-lg rounded-xl shadow-lg">
              {loading ? <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : "Send Reset Link"}
            </Button>
          </form>
        </motion.div>
      );
    }

    // ╔═══════════════════════╗
    // ║   REGISTER            ║
    // ╚═══════════════════════╝
    if (view === "register") {
      return (
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }} key="register">
          <button onClick={() => { setView("login"); clearMessages(); }} className="flex items-center gap-1 text-sm text-purple-600 hover:text-purple-800 font-medium mb-6">
            <ChevronLeft className="w-4 h-4" /> Back to Sign In
          </button>

          <h1 className="text-3xl font-bold text-gray-900 mb-2">Start Your Hiring Engine</h1>
          <p className="text-gray-500 mb-6">Fill in your details and we'll get you set up.</p>

          {error && <div className="mb-4 p-3 bg-red-50 border-l-4 border-red-500 text-red-700 text-sm rounded-r-lg">{error}</div>}
          {success && <div className="mb-4 p-3 bg-green-50 border-l-4 border-green-500 text-green-700 text-sm rounded-r-lg">{success}</div>}

          <form onSubmit={handleRegister} className="space-y-4">
            {/* Name */}
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input placeholder="Your Name *" value={regForm.fullname} onChange={(e) => setRegForm({ ...regForm, fullname: e.target.value })} className="pl-11 h-11 bg-gray-50/50 border-gray-200" />
            </div>

            {/* Email */}
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input type="email" placeholder="Company Email *" value={regForm.email} onChange={(e) => setRegForm({ ...regForm, email: e.target.value })} className="pl-11 h-11 bg-gray-50/50 border-gray-200" />
            </div>

            {/* Company */}
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input placeholder="Company Name *" value={regForm.company} onChange={(e) => setRegForm({ ...regForm, company: e.target.value })} className="pl-11 h-11 bg-gray-50/50 border-gray-200" />
            </div>

            {/* Phone */}
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input type="tel" placeholder="Phone *" value={regForm.phone} onChange={(e) => setRegForm({ ...regForm, phone: e.target.value })} className="pl-11 h-11 bg-gray-50/50 border-gray-200" />
            </div>

            {/* Password */}
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input type={showPassword ? "text" : "password"} placeholder="Create Password *" value={regForm.password} onChange={(e) => setRegForm({ ...regForm, password: e.target.value })} className="pl-11 pr-11 h-11 bg-gray-50/50 border-gray-200" />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600">
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>

            {/* Plan */}
            <select value={regForm.plan} onChange={(e) => setRegForm({ ...regForm, plan: e.target.value })} className="w-full h-11 px-4 rounded-md border border-gray-200 bg-gray-50/50 text-sm text-gray-700 focus:ring-purple-500 focus:border-purple-500">
              <option value="">Select a plan...</option>
              <option value="starter">Starter</option>
              <option value="professional">Professional</option>
              <option value="enterprise">Enterprise</option>
            </select>

            {/* Notes */}
            <textarea placeholder="Notes (optional)" value={regForm.notes} onChange={(e) => setRegForm({ ...regForm, notes: e.target.value })} rows={3} className="w-full px-4 py-3 rounded-md border border-gray-200 bg-gray-50/50 text-sm text-gray-700 resize-none focus:ring-purple-500 focus:border-purple-500" />

            <Button type="submit" disabled={loading} className="w-full h-12 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold text-lg rounded-xl shadow-lg">
              {loading ? <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : "Submit Request"}
            </Button>
          </form>
        </motion.div>
      );
    }

    // ╔═══════════════════════╗
    // ║   LOGIN (default)     ║
    // ╚═══════════════════════╝
    return (
      <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }} key="login">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome back</h1>
        <p className="text-gray-500 mb-8">Sign in to your recruiter workspace</p>

        {error && <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 text-sm flex items-center gap-3 rounded-r-lg"><span className="shrink-0 font-bold text-lg">!</span><p>{error}</p></div>}

        <form onSubmit={handleLogin} className="space-y-5">
          {/* Email */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-gray-700" htmlFor="recruit-email">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input id="recruit-email" type="email" placeholder="name@company.com" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-11 h-12 bg-gray-50/50 border-gray-200 focus:ring-purple-500 focus:border-purple-500" autoComplete="email" />
            </div>
          </div>

          {/* Password */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-gray-700" htmlFor="recruit-password">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input id="recruit-password" type={showPassword ? "text" : "password"} placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} className="pl-11 pr-11 h-12 bg-gray-50/50 border-gray-200 focus:ring-purple-500 focus:border-purple-500" autoComplete="current-password" />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 transition-colors">
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {/* Forgot Password, BELOW password field */}
            <div className="flex justify-end">
              <button type="button" onClick={() => { setView("forgot"); clearMessages(); }} className="text-sm font-semibold text-purple-600 hover:text-purple-800">
                Forgot Password?
              </button>
            </div>
          </div>

          {/* Remember me */}
          <div className="flex items-center space-x-2">
            <Checkbox id="recruit-remember" className="border-gray-300 data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600" />
            <label htmlFor="recruit-remember" className="text-sm font-medium text-gray-500">Remember me</label>
          </div>

          {/* Sign In */}
          <Button type="submit" disabled={loading} className="w-full h-12 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold text-lg rounded-xl shadow-lg shadow-purple-200 hover:shadow-purple-300 transition-all transform hover:-translate-y-0.5 active:translate-y-0">
            {loading ? <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : "Sign In"}
          </Button>
        </form>

        {/* Register link */}
        <div className="mt-8 pt-8 border-t border-gray-100">
          <p className="text-gray-500 text-sm text-center">
            New to WebIdeal Recruit?{" "}
            <button type="button" onClick={() => { setView("register"); clearMessages(); }} className="text-purple-600 font-bold hover:underline">
              Start Your Hiring Engine
            </button>
          </p>
        </div>

        <div className="mt-6 flex justify-center">
          <button type="button" onClick={() => { setView("forgot"); clearMessages(); }} className="text-sm text-gray-400 hover:text-gray-600 font-medium">
            Trouble Logging In?
          </button>
        </div>
      </motion.div>
    );
  };

  // ──────────────────────────────────────────────
  //  RENDER
  // ──────────────────────────────────────────────
  return (
    <div className="min-h-screen flex bg-white font-sans overflow-hidden">

      {/* LEFT SIDE */}
      <div className="w-full lg:w-[45%] flex flex-col p-8 md:p-12 lg:p-16 relative z-10 bg-white overflow-y-auto max-h-screen">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 mb-8 group">
          <div className=" flex items-center justify-center">
            <img src="/logofinal.png" alt="WebIdeal" className="w-7 h-7 object-contain" />
          </div>
          <div>
            <span className="text-xl font-bold text-gray-900">WebIdeal</span>
            <p className="text-[10px] text-purple-600 uppercase tracking-widest font-bold -mt-1">Recruit</p>
          </div>
        </Link>

        {/* Dynamic Content */}
        <div className="flex-1 flex flex-col justify-center max-w-md mx-auto w-full">
          {renderLeftContent()}
        </div>

        {/* Footer */}
        <div className="mt-auto pt-6 text-center lg:text-left">
          <p className="text-xs text-gray-400">&copy; 2026 WebIdeal AI-Smart Hiring. All rights reserved.</p>
        </div>
      </div>

      {/* RIGHT SIDE: BRANDING */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden bg-gradient-to-br from-purple-600 to-pink-600 p-16 xl:p-20 items-center justify-center">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-white/10 rounded-full blur-[100px] animate-pulse" />
          <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-black/10 rounded-full blur-[100px]" />
        </div>

        <div className="relative z-10 w-full max-w-2xl">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
            <div className="flex items-center gap-3 mb-8">
              <div className=" flex items-center justify-center shrink-0">
                <img src="/logofinal.png" alt="WebIdeal" className="w-9 h-9 object-contain" />
              </div>
              <div>
                <span className="text-2xl font-bold text-white tracking-tight">WebIdeal Recruit</span>
                <p className="text-xs text-white/60 uppercase tracking-widest font-semibold">Recruitment Platform</p>
              </div>
            </div>

            <h2 className="text-5xl xl:text-6xl font-black text-white leading-tight mb-4">
              Hire Smarter.<br />Build Faster Teams.
            </h2>
            <p className="text-lg text-white/80 leading-relaxed mb-4 max-w-lg">
              Find, track, and hire top talent with WebIdeal Recruit.
            </p>
            <p className="text-base text-white/70 leading-relaxed mb-10 max-w-lg">
              Streamline your hiring process with intelligent tools designed for recruiters. Manage candidates, schedule interviews, and make better hiring decisions, all in one place.
            </p>

            <div className="grid grid-cols-2 gap-4">
              {features.map((feat, idx) => (
                <motion.div key={idx} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.4 + idx * 0.1 }} whileHover={{ scale: 1.03 }} className="bg-white/10 backdrop-blur-sm border border-white/20 p-5 rounded-2xl cursor-default">
                  <div className="flex items-center gap-2.5 mb-2">
                    <div className="w-8 h-8 bg-white/15 rounded-lg flex items-center justify-center shrink-0">
                      <feat.icon className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-[11px] font-bold text-white/70 uppercase tracking-wider leading-tight">{feat.label}</span>
                  </div>
                  <p className="text-2xl font-bold text-white mb-1">{feat.value}</p>
                  <p className="text-xs text-white/60">{feat.sub}</p>
                </motion.div>
              ))}
            </div>

            <div className="mt-10 pt-8 border-t border-white/20 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-white/60 shrink-0" />
              <p className="text-sm text-white/70 font-medium">Trusted by growing teams and enterprises for faster hiring.</p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
